import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Download, Play, BookOpen, FileText, ExternalLink, Search, Calendar, Mic } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { IMAGES } from "@/data/content";
import api from "@/lib/api";

const CATEGORIES = ["Tous", "Prédications", "Enseignements", "Brochures", "Vidéos"];

// Map plural filter labels to backend singular categories
const FILTER_MAP = {
  "Prédications": "Prédication",
  "Enseignements": "Enseignement",
  "Brochures": "Brochure",
  "Vidéos": "Vidéo",
};

const categoryStyle = (cat) => {
  switch (cat) {
    case "Prédication":
      return { icon: Mic, tone: "bg-[#0B3D91] text-white", action: "Écouter", ActionIcon: Play };
    case "Enseignement":
      return { icon: BookOpen, tone: "bg-[#1C1C1C] text-white", action: "Lire", ActionIcon: ArrowRight };
    case "Brochure":
      return { icon: FileText, tone: "bg-white text-[#0B3D91] border border-[#0B3D91]", action: "Télécharger", ActionIcon: Download };
    case "Vidéo":
      return { icon: Play, tone: "bg-red-600 text-white", action: "Regarder", ActionIcon: Play };
    default:
      return { icon: FileText, tone: "bg-gray-100 text-[#1C1C1C]", action: "Voir", ActionIcon: ArrowRight };
  }
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const resolveAction = (r) => {
  if (r.category === "Vidéo") return { url: r.external_url || "#", label: "Regarder", Icon: Play };
  if (r.category === "Brochure") return { url: r.download_url || r.external_url || "#", label: "Télécharger", Icon: Download };
  if (r.category === "Prédication") return { url: r.external_url || r.download_url || "#", label: "Écouter", Icon: Play };
  return { url: r.external_url || r.download_url || "#", label: "Lire", Icon: ArrowRight };
};

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tous");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/resources")
      .then(({ data }) => setResources(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = resources;
    if (filter !== "Tous") {
      const backendCat = FILTER_MAP[filter];
      list = list.filter((r) => r.category === backendCat);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.title?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q) ||
          r.author?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [resources, filter, query]);

  // Counts per category for the filter buttons
  const counts = useMemo(() => {
    const map = { Tous: resources.length };
    CATEGORIES.slice(1).forEach((c) => {
      map[c] = resources.filter((r) => r.category === FILTER_MAP[c]).length;
    });
    return map;
  }, [resources]);

  return (
    <div data-testid="resources-page">
      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.formation} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Ressources</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Nourrir. Équiper.<br />Édifier.
          </h1>
          <p className="mt-6 text-lg text-blue-50/95 max-w-2xl">
            Une bibliothèque vivante de prédications, enseignements, brochures et vidéos
            pour approfondir votre foi et servir efficacement.
          </p>
        </div>
      </section>

      {/* CATEGORY OVERVIEW */}
      <section className="mv-section bg-white" data-testid="categories-overview">
        <div className="mv-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { key: "Prédications", label: "Prédications", desc: "Messages audio et vidéo.", Icon: Mic },
              { key: "Enseignements", label: "Enseignements", desc: "Études bibliques et guides.", Icon: BookOpen },
              { key: "Brochures", label: "Brochures", desc: "Documents PDF à télécharger.", Icon: FileText },
              { key: "Vidéos", label: "Vidéos", desc: "Reportages et documentaires.", Icon: Play },
            ].map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`overview-${c.key}`}
                className={`group text-left p-6 md:p-7 rounded-2xl border transition-all duration-300 ${
                  filter === c.key
                    ? "bg-[#0B3D91] text-white border-[#0B3D91] shadow-lg"
                    : "bg-white border-gray-200 hover:border-[#0B3D91]/40 hover:shadow-md"
                }`}
              >
                <div className={`h-11 w-11 flex items-center justify-center rounded-lg ${
                  filter === c.key ? "bg-white/15" : "bg-[#0B3D91]/10"
                }`}>
                  <c.Icon size={20} className={filter === c.key ? "text-white" : "text-[#0B3D91]"} />
                </div>
                <div className="mt-5 flex items-baseline justify-between gap-3">
                  <h3 className={`font-heading font-bold text-lg ${filter === c.key ? "text-white" : "text-[#1C1C1C]"}`}>
                    {c.label}
                  </h3>
                  <span className={`text-xs font-bold tracking-widest ${filter === c.key ? "text-blue-100" : "text-[#0B3D91]"}`}>
                    {counts[c.key] || 0}
                  </span>
                </div>
                <p className={`mt-1.5 text-sm ${filter === c.key ? "text-blue-100" : "text-muted"} leading-relaxed`}>
                  {c.desc}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FILTERS + SEARCH */}
      <section className="pt-2 pb-6 bg-[#F8FAFC] border-t border-gray-200" data-testid="filters-bar">
        <div className="mv-container flex flex-col md:flex-row md:items-center justify-between gap-4 pt-8">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${
                  filter === c
                    ? "bg-[#0B3D91] text-white border-[#0B3D91]"
                    : "bg-white text-[#1C1C1C] border-gray-300 hover:border-[#0B3D91]"
                }`}
              >
                {c} {counts[c] !== undefined && <span className="ml-1 opacity-70">· {counts[c]}</span>}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Rechercher une ressource..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="resources-search"
              className="w-full bg-white border border-gray-300 pl-9 pr-4 py-2.5 text-sm rounded-full focus:outline-none focus:border-[#0B3D91]"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="pt-6 pb-24 bg-[#F8FAFC]" data-testid="resources-grid-section">
        <div className="mv-container">
          {loading ? (
            <div className="text-center py-20 text-muted">Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-gray-300 rounded-2xl bg-white">
              <FileText size={36} className="mx-auto text-gray-400" />
              <p className="mt-4 text-muted">Aucune ressource ne correspond à votre recherche.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r) => {
                const style = categoryStyle(r.category);
                const CatIcon = style.icon;
                const action = resolveAction(r);
                const ActionIcon = action.Icon;
                return (
                  <article
                    key={r.id}
                    data-testid={`resource-card-${r.id}`}
                    className="group flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    {/* Thumbnail (or icon fallback) */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-[#0B3D91]/5">
                      {r.thumbnail_url ? (
                        <img
                          src={r.thumbnail_url}
                          alt={r.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CatIcon size={48} className="text-[#0B3D91]/50" />
                        </div>
                      )}
                      {/* Category badge */}
                      <div className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-full ${style.tone}`}>
                        <CatIcon size={12} />
                        {r.category}
                      </div>
                      {/* Play overlay for videos */}
                      {r.category === "Vidéo" && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="h-16 w-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                            <Play size={24} className="text-[#0B3D91] ml-1" fill="currentColor" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading font-bold text-xl text-[#1C1C1C] leading-tight">
                        {r.title}
                      </h3>
                      <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-3 flex-1">
                        {r.description}
                      </p>

                      <div className="mt-5 flex items-center gap-3 text-xs text-muted">
                        <span className="font-bold text-[#1C1C1C]">{r.author || "MIREVSA"}</span>
                        {r.published_date && (
                          <>
                            <span className="opacity-40">·</span>
                            <span className="inline-flex items-center gap-1">
                              <Calendar size={12} /> {formatDate(r.published_date)}
                            </span>
                          </>
                        )}
                      </div>

                      <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between">
                        <a
                          href={action.url}
                          target={action.url && action.url !== "#" ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          data-testid={`resource-action-${r.id}`}
                          className="inline-flex items-center gap-2 text-sm font-bold text-[#0B3D91] hover:gap-3 transition-all"
                        >
                          <ActionIcon size={16} /> {action.label}
                        </a>
                        {r.external_url && r.external_url !== "#" && (
                          <ExternalLink size={14} className="text-muted" />
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B3D91] text-white py-20" data-testid="resources-cta">
        <div className="mv-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-white/80" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold">CEFOBIM</span>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl">
              Poursuivez votre formation avec le CEFOBIM.
            </h2>
            <p className="mt-5 text-lg text-blue-100 max-w-2xl leading-relaxed">
              Rejoignez notre école biblique 100% gratuite via WhatsApp — plus de 600 étudiants,
              une vingtaine de professeurs bénévoles, et un contenu structuré pour tous les niveaux.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href="/join"
              className="bg-white text-[#0B3D91] hover:bg-blue-50 inline-flex items-center justify-between gap-2 px-6 py-4 font-bold transition-all rounded-lg"
              data-testid="cefobim-join"
            >
              S'inscrire au CEFOBIM <ArrowRight size={18} />
            </a>
            <a
              href="/contact"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B3D91] inline-flex items-center justify-between gap-2 px-6 py-4 font-bold transition-all rounded-lg"
            >
              Poser une question <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
